<template>
  <div class="paciente-list-item">
    <paciente-card
      :paciente="paciente"
      :list-index="listIndex"
      :is-espera="isEspera"
      :is-selected="isSelected"
      @click="onToggle"
    >
      <template #actions>
        <div class="actions-row">
          <v-btn
            v-if="isAtendido"
            variant="flat"
            color="info"
            class="action-btn"
            :disabled="disabled"
            @click.stop="abrirProntuario"
          >
            <span class="material-icons btn-icon">visibility</span>
            Visualizar
          </v-btn>
          <template v-else>
            <v-btn
              variant="flat"
              color="primary"
              class="action-btn"
              :disabled="disabled || !canCall"
              :loading="calling"
              @click.stop="fazerChamada"
            >
              <span class="material-icons btn-icon">record_voice_over</span>
              <template v-if="callCooldownActive">
                {{ callCooldownTimer }}s
              </template>
              <template v-else>
                Chamar
              </template>
            </v-btn>

            <v-btn
              variant="flat"
              color="secondary"
              class="action-btn"
              :disabled="disabled || !canStartAttendance"
              :loading="startingAttendance"
              @click.stop="iniciarAtendimento"
            >
              <span class="material-icons btn-icon">play_arrow</span>
              Iniciar
            </v-btn>
          </template>
        </div>
      </template>
    </paciente-card>
  </div>
</template>

<script>
import medicoService from '../../services/medico.service'
import pacienteCard from './pacienteCard.vue'

export default {
  name: 'PacienteListItem',
  components: {
    pacienteCard
  },
  props: {
    paciente: {
      type: Object,
      required: true
    },
    listIndex: {
      type: Number,
      default: null
    },
    isEspera: {
      type: Boolean,
      default: false
    },
    isPendente: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'toggle',
    'chamada-realizada',
    'chamada-expirada',
    'iniciou-atendimento',
    'observar-prontuario'
  ],
  data() {
    return {
      calling: false,
      startingAttendance: false,
      fezChamada: false,
      blockMethod: false,
      callCooldownActive: false,
      callCooldownTimer: 0,
      callCooldownIntervalId: null,
      intervalContagemRegressiva: null,
      secondsRegressivo: 10
    }
  },
  computed: {
    user() {
      return this.$store.getters.usuario
    },
    isAtendido() {
      return this.paciente?.status === 4
    },
    canCall() {
      return !this.calling &&
        !this.callCooldownActive &&
        !this.isAtendido
    },
    canStartAttendance() {
      return !this.blockMethod && !this.isAtendido
    }
  },
  watch: {
    paciente: {
      handler(novoPaciente) {
        if (!novoPaciente) return
        if (this.isEspera || this.isPendente) {
          this.fezChamada = true
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    this.limparIntervalos()
  },
  methods: {
    getPrioridadePaciente() {
      const direto = this.paciente?.prioridade
      if (direto !== null && direto !== undefined && direto !== '') {
        const num = Number(direto)
        return Number.isNaN(num) ? null : num
      }
      const atendimentos = this.paciente?.atendimentos || []
      for (const atendimento of atendimentos) {
        const valor = atendimento?.prioridade
        if (valor !== null && valor !== undefined && valor !== '') {
          const num = Number(valor)
          return Number.isNaN(num) ? null : num
        }
      }
      return null
    },

    onToggle() {
      if (this.disabled) return
      this.$emit('toggle', this.paciente)
    },

    async fazerChamada() {
      if (this.calling || this.callCooldownActive) return

      this.$emit('toggle', this.paciente)
      this.calling = true
      this.callCooldownActive = true
      this.callCooldownTimer = 5

      this.callCooldownIntervalId = setInterval(() => {
        this.callCooldownTimer--
        if (this.callCooldownTimer <= 0) {
          clearInterval(this.callCooldownIntervalId)
          this.callCooldownIntervalId = null
          this.callCooldownActive = false
          this.callCooldownTimer = 0
        }
      }, 1000)

      try {
        const response = await medicoService.medico.fazerChamada(
          this.paciente.idAtendimento,
          this.paciente.codigo,
          this.user.nomeConsultorio,
          this.paciente.idConsulta,
          this.user.nome,
          this.paciente.nomePaciente,
          this.getPrioridadePaciente(),
          `${this.user.idLocal}`,
          this.user.nomeLocal,
          this.user.tecnico,
          false,
          this.user.idLocal,
          this.paciente.idPaciente,
          this.paciente.dataNascimento
        )

        if (response.data === true) {
          this.fezChamada = true
          this.iniciarCronometroRegressivo()

          this.$emit('chamada-realizada', {
            idAtendimento: this.paciente.idAtendimento,
            status: this.isEspera ? 3 : this.paciente.status,
            origemChamada: this.isEspera ? 'espera' : null
          })
        }
      } catch (error) {
        console.error('Erro ao fazer chamada:', error)
      } finally {
        this.calling = false
      }
    },

    iniciarCronometroRegressivo() {
      if (this.intervalContagemRegressiva) return
      this.secondsRegressivo = 10
      this.intervalContagemRegressiva = setInterval(() => {
        this.secondsRegressivo--
        if (this.secondsRegressivo < 0) {
          clearInterval(this.intervalContagemRegressiva)
          this.intervalContagemRegressiva = null
          this.$emit('chamada-expirada', this.paciente)
        }
      }, 1000)
    },

    async iniciarAtendimento() {
      if (this.blockMethod) return
      this.$emit('toggle', this.paciente)
      this.startingAttendance = true
      this.blockMethod = true

      try {
        const response = await medicoService.medico.iniciarAtendimento(
          this.paciente.idAtendimento,
          this.paciente.codigo,
          this.paciente.idConsulta
        )

        if (response.status === 200 && response.data) {
          this.$emit('iniciou-atendimento', this.paciente)
        }
      } catch (error) {
        console.error('Erro ao iniciar atendimento:', error)
      } finally {
        this.startingAttendance = false
        this.blockMethod = false
      }
    },

    limparIntervalos() {
      if (this.intervalContagemRegressiva) {
        clearInterval(this.intervalContagemRegressiva)
        this.intervalContagemRegressiva = null
      }
      if (this.callCooldownIntervalId) {
        clearInterval(this.callCooldownIntervalId)
        this.callCooldownIntervalId = null
      }
    },
    abrirProntuario() {
      if (this.disabled) return
      this.$emit('observar-prontuario', this.paciente)
    }
  }
}
</script>

<style scoped>
.paciente-list-item {
  display: flex;
  flex-direction: column;
}

.actions-row {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: flex-end;
}

.action-btn {
  min-width: 130px;
  height: 36px;
}

.btn-icon {
  font-size: 16px;
  margin-right: 4px;
}
</style>
